// Rollable tables at the tabletop (issue 1158): the pure half. A table is a name and weighted outcomes, each with its
// text and, optionally, the creature it names; a DM keeps their own (`vtt.user_rolltable`, account-wide, like the dice
// bag) and the site ships some (the `rolltable` family's entries, read with the DM's grant). Rolling one is an ordinary
// `roll` in the table's log — `RollRecord.table` beside the face — so privacy, the log page, the history and the saved
// file are the dice bag's own: a private table roll reaches no player, by the same rules as any private throw.
//
// The pick is a single die with as many sides as the table's total weight, thrown by the server's generator, and the
// outcome is the one whose running weight the face falls in: outcome i covers faces (w0+…+w(i-1), w0+…+wi]. So a
// scripted generator picks a known outcome, and the face in the record proves which.
export const TABLE_LIMITS = {
    /** A person's own tables. */
    tables: 50,
    /** Outcomes on one table. */
    outcomes: 100,
    name: 80,
    text: 300,
    /** A DM's outcome weighs 1 to 100. */
    weight: 100,
    /** The die a roll throws: the total weight, never more than the dice bag's largest die. */
    sides: 10_000,
};
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const TEXT_BAD = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
const KEY_RE = /^[a-z0-9][a-z0-9._-]{0,199}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const onlyKeys = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
/** Text with no control character, 1 to `max` long after trimming; else null. */
function line(v, max) {
    if (typeof v !== 'string' || CONTROL_RE.test(v))
        return null;
    const s = v.trim();
    return s.length >= 1 && s.length <= max ? s : null;
}
export const tableName = (v) => line(v, TABLE_LIMITS.name);
export function parseTableRef(v) {
    if (!isObj(v) || !onlyKeys(v, ['segment', 'slug', 'name']))
        return null;
    const name = line(v.name, 120);
    if (typeof v.segment !== 'string' || typeof v.slug !== 'string' || !KEY_RE.test(v.segment) || !KEY_RE.test(v.slug) || !name)
        return null;
    return { segment: v.segment, slug: v.slug, name };
}
/** One of a DM's outcomes: text, a whole weight from 1 to 100, an optional creature; nothing else. */
export function parseOutcome(v) {
    if (!isObj(v) || !onlyKeys(v, ['text', 'weight', 'ref']))
        return null;
    const text = line(v.text, TABLE_LIMITS.text);
    const w = v.weight;
    if (!text || typeof w !== 'number' || !Number.isInteger(w) || w < 1 || w > TABLE_LIMITS.weight)
        return null;
    if (v.ref === undefined || v.ref === null)
        return { text, weight: w };
    const ref = parseTableRef(v.ref);
    return ref ? { text, weight: w, ref } : null;
}
/** A DM's table as sent or stored: a name and 1 to 100 outcomes, exactly; else null whole. */
export function parseUserTable(v) {
    if (!isObj(v) || !onlyKeys(v, ['name', 'outcomes']))
        return null;
    const name = tableName(v.name);
    if (!name || !Array.isArray(v.outcomes) || v.outcomes.length < 1 || v.outcomes.length > TABLE_LIMITS.outcomes)
        return null;
    const outcomes = [];
    for (const o of v.outcomes) {
        const p = parseOutcome(o);
        if (!p)
            return null;
        outcomes.push(p);
    }
    return { name, outcomes };
}
/** Which table to roll, from the wire: exactly a site slug or one of the DM's own ids. */
export function parseTableSource(v) {
    if (!isObj(v))
        return null;
    if (v.source === 'site' && onlyKeys(v, ['source', 'slug']) && typeof v.slug === 'string' && KEY_RE.test(v.slug))
        return { source: 'site', slug: v.slug };
    if (v.source === 'mine' && onlyKeys(v, ['source', 'id']) && typeof v.id === 'string' && UUID_RE.test(v.id))
        return { source: 'mine', id: v.id.toLowerCase() };
    return null;
}
/**
 * A site table's outcomes made rollable: the site allows any weight of 0 or more, fractions included (its roller,
 * issue 311). An outcome weighing 0 is never drawn, so it is left out; fractional weights are scaled by 100 and
 * rounded (never below 1). Null when nothing can be drawn or the total is past the largest die.
 */
export function rollableWeights(weights) {
    if (!weights.length || weights.some((w) => typeof w !== 'number' || !Number.isFinite(w) || w < 0))
        return null;
    const whole = weights.every((w) => Number.isInteger(w));
    const out = weights.map((w) => (w === 0 ? 0 : whole ? w : Math.max(1, Math.round(w * 100))));
    const total = out.reduce((a, b) => a + b, 0);
    return total >= 1 && total <= TABLE_LIMITS.sides ? out : null;
}
/** The outcome a face picks: the one whose running weight the face falls in. */
export function outcomeAt(weights, face) {
    let run = 0;
    for (let i = 0; i < weights.length; i += 1) {
        run += weights[i];
        if (face <= run)
            return i;
    }
    return weights.length - 1;
}
/** Roll a table: one die of the total weight, from the server's generator, and the outcome its face falls in. */
export function pickOutcome(rng, weights) {
    const sides = weights.reduce((a, b) => a + b, 0);
    const face = rng(sides);
    return { index: outcomeAt(weights, face), face, sides };
}
/**
 * A table rolled into a log record: the die thrown, its face, and `table` naming the table and the outcome (with its
 * creature, the DM's to place). Private or public as the dice bag is set; the label is the table's name.
 */
export function rollTableWith(rng, table, priv, actor, id, at) {
    const { index, face, sides } = pickOutcome(rng, table.outcomes.map((o) => o.weight));
    const o = table.outcomes[index];
    return {
        id, notation: `1d${sides}`, die: { kind: 'd', sides }, dice: [face], modifier: 0, total: face,
        actor, by: null, label: table.name, private: priv, at, fudged: null,
        table: { name: table.name, text: o.text, ...(o.ref ? { ref: { ...o.ref } } : {}) },
    };
}
export function parseTableFacts(v) {
    if (!isObj(v) || !onlyKeys(v, ['name', 'text', 'ref']))
        return null;
    const name = line(v.name, TABLE_LIMITS.name);
    // Plain text: line breaks and tabs stand, no other control character (the review of 2026-10-10: a NUL was accepted).
    const text = typeof v.text === 'string' && v.text.length >= 1 && v.text.length <= 2000 && !TEXT_BAD.test(v.text) ? v.text : null;
    if (!name || !text)
        return null;
    if (v.ref === undefined)
        return { name, text };
    const ref = parseTableRef(v.ref);
    return ref ? { name, text, ref } : null;
}
/** The table's line as anyone may read it: the table and what came up, never the creature the DM may place. */
export function playerTableFacts(t) {
    return { name: t.name, text: t.text };
}
export function parseTableRollRequest(v) {
    if (!isObj(v) || v.kind !== 'table.roll' || !onlyKeys(v, ['kind', 'private']) || typeof v.private !== 'boolean')
        return null;
    return { kind: 'table.roll', private: v.private };
}
//# sourceMappingURL=tables.js.map